import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

const FreeEstimateForm = ({ apiUrl }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    projectNeeds: '',
    additionalInfo: '',
  });
  const recaptchaRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaVisible, setRecaptchaVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const [formReady, setFormReady] = useState(false);

  // 🔹 Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    // Only require key fields for reCAPTCHA visibility
    const requiredFieldsFilled = ['firstName', 'email', 'phone', 'projectNeeds'].every(
      (key) => updatedFormData[key].trim() !== ''
    );
    setRecaptchaVisible(requiredFieldsFilled);
    setFormReady(requiredFieldsFilled && !!recaptchaRef.current?.getValue());
  };

  const handleRecaptchaChange = (token) => {
    const requiredFieldsFilled = ['firstName', 'email', 'phone', 'projectNeeds'].every(
      (key) => formData[key].trim() !== ''
    );
    setFormReady(requiredFieldsFilled && !!token);
  };

  const handleRecaptchaExpired = () => {
    setFormReady(false);
    recaptchaRef.current?.reset();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName || formData.firstName.length > 100)
      newErrors.firstName = 'First name is required and must be under 100 characters.';
    if (formData.lastName.length > 100)
      newErrors.lastName = 'Last name must be under 100 characters.';
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Please enter a valid email address.';
    if (!formData.phone || !/^\d{10,11}$/.test(formData.phone))
      newErrors.phone = 'Phone number should be 10–11 digits.';
    if (formData.address.length > 250)
      newErrors.address = 'Address must be under 250 characters.';
    if (!formData.projectNeeds || formData.projectNeeds.length > 8000)
      newErrors.projectNeeds = 'Please describe your project (under 8000 characters).';
    if (formData.additionalInfo.length > 8000)
      newErrors.additionalInfo = 'Additional info must be under 8000 characters.';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    const token = recaptchaRef.current?.getValue();
    if (!token) {
      alert('Please complete the reCAPTCHA verification.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${apiUrl}/api/public/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          recaptchaToken: token,
        }),
      });

      if (response.ok) {
        alert('Your free estimate request has been sent successfully!');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          projectNeeds: '',
          additionalInfo: '',
        });
        setErrors({});
        recaptchaRef.current?.reset();
        setRecaptchaVisible(false);
        setFormReady(false);
      } else {
        const errorData = await response.json();
        alert('Error: ' + (errorData.message || 'Submission failed.'));
      }
    } catch (err) {
      console.error('Error submitting estimate form:', err);
      alert('There was a problem sending your request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="estimate-form" onSubmit={handleSubmit}>
      {/* --- Required --- */}
      <div className="contact-form-input-wrapper half">
        <label>First Name</label>
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          required
          maxLength={100}
        />
        {errors.firstName && <div className="error-message">{errors.firstName}</div>}
      </div>

      <div className="contact-form-input-wrapper half">
        <label>Last Name</label>
        <input
          type="text"
          name="lastName"
          placeholder="Optional"
          value={formData.lastName}
          onChange={handleChange}
          maxLength={100}
        />
        {errors.lastName && <div className="error-message">{errors.lastName}</div>}
      </div>

      <div className="contact-form-input-wrapper half">
        <label>Email</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          maxLength={150}
        />
        {errors.email && <div className="error-message">{errors.email}</div>}
      </div>

      <div className="contact-form-input-wrapper half">
        <label>Phone</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          inputMode="numeric"
          pattern="\d{10,11}"
          maxLength={11}
          required
        />
        {errors.phone && <div className="error-message">{errors.phone}</div>}
      </div>

      <div className="contact-form-input-wrapper full">
        <label>Address</label>
        <input
          type="text"
          name="address"
          placeholder="Optional"
          value={formData.address}
          onChange={handleChange}
          maxLength={250}
        />
        {errors.address && <div className="error-message">{errors.address}</div>}
      </div>

      <div className="contact-form-input-wrapper full">
        <label>Project Needs</label>
        <textarea
          name="projectNeeds"
          value={formData.projectNeeds}
          onChange={handleChange}
          required
          maxLength={8000}
          placeholder=""
        />
        {errors.projectNeeds && <div className="error-message">{errors.projectNeeds}</div>}
      </div>

      <div className="contact-form-input-wrapper full">
        <label>Additional Information</label>
        <textarea
          name="additionalInfo"
          value={formData.additionalInfo}
          onChange={handleChange}
          maxLength={8000}
          placeholder="Optional"
        />
        {errors.additionalInfo && <div className="error-message">{errors.additionalInfo}</div>}
      </div>

      {recaptchaVisible && (
        <div className="captcha-wrapper">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
            onChange={handleRecaptchaChange}
            onExpired={handleRecaptchaExpired}
          />
        </div>
      )}

      <div className="form-submit-wrapper">
        <button
          type="submit"
          className="estimate-button"
          disabled={isSubmitting || !formReady}
        >
          {isSubmitting ? 'Submitting...' : 'Request Free Estimate'}
        </button>
      </div>
    </form>
  );
};

export default FreeEstimateForm;
